import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  RotateCcw, 
  Check, 
  AlertTriangle, 
  X as XIcon,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Info,
  Scale,
  FileText,
  Share2,
  Download,
  Copy,
  Loader2,
  CheckCircle,
  SlidersHorizontal,
  ArrowUpDown,
  Filter,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { useWizard } from './WizardContext';
import { useSaveShare } from '@/hooks/useSaveShare';
import { exportResultsToPDF } from '@/utils/pdfExport';
import { analyticsApi } from '@/services/api';
import { cn } from '@/lib/utils';

// Pagination config
const RESULTS_PER_PAGE = 15;

const CostIndicator = ({ level }) => {
  const symbols = ['Free', '£', '££', '£££'];
  return (
    <span className="text-sm font-medium text-muted-foreground">
      {symbols[level - 1] || '—'}
    </span>
  );
};

const SuitabilityBadge = ({ suitability }) => {
  const config = {
    suitable: {
      icon: Check,
      label: 'Suitable',
      className: 'bg-success-light text-success border-success/20',
    },
    caution: {
      icon: AlertTriangle,
      label: 'Use with caution',
      className: 'bg-warning-light border-warning/20',
      textClass: 'text-amber-700',
    },
    'not-recommended': {
      icon: XIcon,
      label: 'Not recommended',
      className: 'bg-destructive-light text-destructive border-destructive/20',
    },
  };

  const { icon: Icon, label, className, textClass } = config[suitability] || config.caution;

  return (
    <div className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border', className, textClass)}>
      <Icon className="w-3 h-3" />
      {label}
    </div>
  );
};

const DataHandlingIndicator = ({ level }) => {
  const config = {
    clear: { label: 'Clear data policies', className: 'text-success' },
    moderate: { label: 'Standard policies', className: 'text-muted-foreground' },
    unclear: { label: 'Review policies carefully', className: 'text-warning' },
  };

  const { label, className } = config[level] || config.moderate;

  return (
    <span className={cn('text-xs', className)}>{label}</span>
  );
};

const PlatformCard = ({ platform, isSelected }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { toggleComparison, navigateToPlatform } = useWizard();
  const isFictional = platform.tags?.includes('fictional');

  return (
    <Card className={cn(
      'transition-all duration-200 flex flex-col',
      isSelected && 'ring-2 ring-primary ring-offset-2'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-lg truncate">{platform.name}</CardTitle>
              {isFictional && (
                <Badge variant="outline" className="text-xs shrink-0">
                  Example
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{platform.provider}</p>
          </div>
          <SuitabilityBadge suitability={platform.suitability} />
        </div>
      </CardHeader>

      <CardContent className="flex flex-col flex-1">
        {/* Summary */}
        <p className="text-sm text-foreground leading-relaxed mb-4">
          {platform.summary}
        </p>

        {/* Quick info */}
        <div className="flex items-center gap-4 text-sm mb-4">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Cost:</span>
            <CostIndicator level={platform.costBand} />
          </div>
          <div className="h-4 w-px bg-border" />
          <DataHandlingIndicator level={platform.dataHandling?.level || 'moderate'} />
        </div>

        {/* Expandable reasons */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-primary hover:underline mb-4"
        >
          {isExpanded ? 'Hide details' : 'Why this result?'}
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {isExpanded && (
          <div className="space-y-3 mb-4 animate-fade-in">
            {/* Why suitable */}
            {platform.reasons?.suitable?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-success flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Why this might suit you
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                  {platform.reasons.suitable.map((reason, i) => (
                    <li key={i} className="list-disc list-outside">{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Concerns */}
            {platform.reasons?.concerns?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  What to be aware of
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                  {platform.reasons.concerns.map((concern, i) => (
                    <li key={i} className="list-disc list-outside">{concern}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Actions - pushed to bottom */}
        <div className="flex items-center gap-2 mt-auto pt-2">
          <Button
            variant={isSelected ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleComparison(platform.id)}
            className="gap-1.5"
          >
            <Scale className="w-4 h-4" />
            {isSelected ? 'Selected' : 'Compare'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateToPlatform(platform.id)}
            className="gap-1.5"
          >
            <FileText className="w-4 h-4" />
            View details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Sort options
const SORT_OPTIONS = [
  { value: 'suitability', label: 'Best Match', icon: Check },
  { value: 'name-asc', label: 'Name (A-Z)', icon: ArrowUp },
  { value: 'name-desc', label: 'Name (Z-A)', icon: ArrowDown },
  { value: 'cost-asc', label: 'Cost (Low to High)', icon: ArrowUp },
  { value: 'cost-desc', label: 'Cost (High to Low)', icon: ArrowDown },
];

// Filter labels
const SUITABILITY_LABELS = {
  suitable: 'Suitable',
  caution: 'Use with Caution',
  'not-recommended': 'Not Recommended',
};

const COST_LABELS = {
  1: 'Free',
  2: 'Low Cost (£)',
  3: 'Medium Cost (££)',
  4: 'High Cost (£££)',
};

export const ResultsScreen = () => {
  const { 
    results, 
    answers,
    goToPrevious, 
    resetWizard, 
    selectedForComparison,
    clearComparison,
    navigateToComparison
  } = useWizard();
  
  const { isSaving, shareCode, saveResults, getShareUrl, copyShareLink, clearShare } = useSaveShare();
  const [copied, setCopied] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Sorting and filtering state
  const [sortBy, setSortBy] = useState('suitability');
  const [filters, setFilters] = useState({
    suitability: [], // empty = all
    costBand: [],   // empty = all
    hasFreeTier: false,
    hasApiAccess: false,
  });

  // Calculate available filter options based on results
  const filterOptions = useMemo(() => {
    const suitabilitySet = new Set(results.map(r => r.suitability));
    const costSet = new Set(results.map(r => r.costBand));
    
    return {
      suitability: Array.from(suitabilitySet),
      costBand: Array.from(costSet).sort((a, b) => a - b),
      hasAnyFreeTier: results.some(r => r.pricing?.hasFreeeTier || r.pricing?.hasFreeTier),
      hasAnyApiAccess: results.some(r => r.features?.apiAccess),
    };
  }, [results]);

  // Filter and sort results
  const filteredAndSortedResults = useMemo(() => {
    let filtered = [...results];
    
    // Apply suitability filter
    if (filters.suitability.length > 0) {
      filtered = filtered.filter(p => filters.suitability.includes(p.suitability));
    }
    
    // Apply cost filter
    if (filters.costBand.length > 0) {
      filtered = filtered.filter(p => filters.costBand.includes(p.costBand));
    }
    
    // Apply feature filters
    if (filters.hasFreeTier) {
      filtered = filtered.filter(p => p.pricing?.hasFreeeTier || p.pricing?.hasFreeTier);
    }
    
    if (filters.hasApiAccess) {
      filtered = filtered.filter(p => p.features?.apiAccess);
    }
    
    // Sort
    switch (sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'cost-asc':
        filtered.sort((a, b) => (a.costBand || 2) - (b.costBand || 2));
        break;
      case 'cost-desc':
        filtered.sort((a, b) => (b.costBand || 2) - (a.costBand || 2));
        break;
      case 'suitability':
      default:
        // Already sorted by suitability from calculateResults
        const suitabilityOrder = { suitable: 0, caution: 1, 'not-recommended': 2 };
        filtered.sort((a, b) => {
          if (suitabilityOrder[a.suitability] !== suitabilityOrder[b.suitability]) {
            return suitabilityOrder[a.suitability] - suitabilityOrder[b.suitability];
          }
          return (b.percentage || 0) - (a.percentage || 0);
        });
        break;
    }
    
    return filtered;
  }, [results, sortBy, filters]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedResults.length / RESULTS_PER_PAGE);
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * RESULTS_PER_PAGE;
    return filteredAndSortedResults.slice(startIndex, startIndex + RESULTS_PER_PAGE);
  }, [filteredAndSortedResults, currentPage]);

  // Reset to page 1 when filters change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.suitability.length > 0) count++;
    if (filters.costBand.length > 0) count++;
    if (filters.hasFreeTier) count++;
    if (filters.hasApiAccess) count++;
    return count;
  }, [filters]);

  const toggleSuitabilityFilter = (value) => {
    setFilters(prev => ({
      ...prev,
      suitability: prev.suitability.includes(value)
        ? prev.suitability.filter(v => v !== value)
        : [...prev.suitability, value]
    }));
    setCurrentPage(1);
  };

  const toggleCostFilter = (value) => {
    setFilters(prev => ({
      ...prev,
      costBand: prev.costBand.includes(value)
        ? prev.costBand.filter(v => v !== value)
        : [...prev.costBand, value]
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      suitability: [],
      costBand: [],
      hasFreeTier: false,
      hasApiAccess: false,
    });
    setCurrentPage(1);
  };

  const suitableCount = results.filter(r => r.suitability === 'suitable').length;
  const cautionCount = results.filter(r => r.suitability === 'caution').length;

  // Track comparison inclusions when results are displayed (only once per session per platform)
  useEffect(() => {
    if (paginatedResults.length > 0) {
      // Get already tracked platforms from session
      const trackedKey = 'tracked_comparison_inclusions';
      const tracked = new Set(JSON.parse(sessionStorage.getItem(trackedKey) || '[]'));
      
      // Track each platform that appears in results (only if not already tracked)
      paginatedResults.forEach(platform => {
        if (!tracked.has(platform.id)) {
          analyticsApi.trackVendor('comparison_inclusion', platform.id, 'wizard_results');
          tracked.add(platform.id);
        }
      });
      
      // Save tracked platforms to session
      sessionStorage.setItem(trackedKey, JSON.stringify([...tracked]));
    }
  }, [paginatedResults]);

  const handleSaveShare = async () => {
    await saveResults(answers, results, []);
  };

  const handleCopyLink = async () => {
    if (shareCode) {
      const success = await copyShareLink(shareCode);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleExportPDF = () => {
    exportResultsToPDF(results, answers);
  };

  return (
    <div className="animate-fade-in">
      {/* Share Success Message */}
      {shareCode && (
        <Card className="mb-4 sm:mb-6 bg-success/5 border-success/20">
          <CardContent className="py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Results saved!</p>
                  <p className="text-xs text-muted-foreground">Share this link with others</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-8 sm:ml-0">
                <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-[150px] sm:max-w-[200px]">
                  {getShareUrl(shareCode)}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="gap-1 flex-shrink-0"
                >
                  {copied ? <CheckCircle className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearShare}
                  className="px-2 flex-shrink-0"
                >
                  <XIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 mb-2">
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            Your results
          </h2>
          
          {/* Action Buttons - scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Button
              variant="outline"
              size="sm"
              onClick={resetWizard}
              className="gap-1.5 whitespace-nowrap flex-shrink-0"
              data-testid="results-start-over-top-btn"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Start over</span>
            </Button>
            
            {!shareCode && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveShare}
                disabled={isSaving || results.length === 0}
                className="gap-1.5 whitespace-nowrap flex-shrink-0"
                data-testid="results-save-share-btn"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
                <span className="hidden xs:inline">{isSaving ? 'Saving...' : 'Save & Share'}</span>
                <span className="xs:hidden">{isSaving ? '...' : 'Share'}</span>
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={results.length === 0}
              className="gap-1.5 whitespace-nowrap flex-shrink-0"
              data-testid="results-export-pdf-btn"
            >
              <Download className="w-4 h-4" />
              <span className="hidden xs:inline">Export PDF</span>
              <span className="xs:hidden">PDF</span>
            </Button>
          </div>
        </div>
        
        <p className="text-sm sm:text-base text-muted-foreground mb-4">
          Based on the information you provided, here are platforms that may be relevant to your needs.
        </p>
        
        {/* Summary stats */}
        <div className="flex items-center gap-4 text-sm">
          {suitableCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-success" />
              {suitableCount} suitable
            </span>
          )}
          {cautionCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-warning" />
              {cautionCount} with caveats
            </span>
          )}
        </div>
      </div>

      {/* Sorting & Filtering Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="sort-dropdown">
                <ArrowUpDown className="w-4 h-4" />
                <span className="hidden sm:inline">Sort:</span>
                <span className="text-muted-foreground">
                  {SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Best Match'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={sortBy} onValueChange={(value) => { setSortBy(value); setCurrentPage(1); }}>
                {SORT_OPTIONS.map(option => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    <option.icon className="w-4 h-4 mr-2" />
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filter Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                data-testid="filter-dropdown"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Filter results</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Suitability filters */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Suitability
              </DropdownMenuLabel>
              {filterOptions.suitability.map(suit => (
                <DropdownMenuCheckboxItem
                  key={suit}
                  checked={filters.suitability.includes(suit)}
                  onCheckedChange={() => toggleSuitabilityFilter(suit)}
                >
                  {SUITABILITY_LABELS[suit] || suit}
                </DropdownMenuCheckboxItem>
              ))}
              
              <DropdownMenuSeparator />
              
              {/* Cost filters */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Cost Band
              </DropdownMenuLabel>
              {filterOptions.costBand.map(cost => (
                <DropdownMenuCheckboxItem
                  key={cost}
                  checked={filters.costBand.includes(cost)}
                  onCheckedChange={() => toggleCostFilter(cost)}
                >
                  {COST_LABELS[cost] || `Level ${cost}`}
                </DropdownMenuCheckboxItem>
              ))}
              
              <DropdownMenuSeparator />
              
              {/* Feature filters */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Features
              </DropdownMenuLabel>
              {filterOptions.hasAnyFreeTier && (
                <DropdownMenuCheckboxItem
                  checked={filters.hasFreeTier}
                  onCheckedChange={(checked) => { setFilters(prev => ({ ...prev, hasFreeTier: checked })); setCurrentPage(1); }}
                >
                  Has Free Tier
                </DropdownMenuCheckboxItem>
              )}
              {filterOptions.hasAnyApiAccess && (
                <DropdownMenuCheckboxItem
                  checked={filters.hasApiAccess}
                  onCheckedChange={(checked) => { setFilters(prev => ({ ...prev, hasApiAccess: checked })); setCurrentPage(1); }}
                >
                  Has API Access
                </DropdownMenuCheckboxItem>
              )}
              
              {activeFilterCount > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full justify-center text-destructive"
                      onClick={clearFilters}
                    >
                      Clear all filters
                    </Button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Results count */}
        <span className="text-sm text-muted-foreground">
          Showing {paginatedResults.length} of {filteredAndSortedResults.length} platforms
          {filteredAndSortedResults.length !== results.length && ` (${results.length} total)`}
        </span>
      </div>

      {/* Active filter badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {filters.suitability.map(suit => (
            <Badge 
              key={suit} 
              variant="secondary" 
              className="gap-1 cursor-pointer hover:bg-destructive/10"
              onClick={() => toggleSuitabilityFilter(suit)}
            >
              {SUITABILITY_LABELS[suit]}
              <XIcon className="w-3 h-3" />
            </Badge>
          ))}
          {filters.costBand.map(cost => (
            <Badge 
              key={cost} 
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive/10"
              onClick={() => toggleCostFilter(cost)}
            >
              {COST_LABELS[cost]}
              <XIcon className="w-3 h-3" />
            </Badge>
          ))}
          {filters.hasFreeTier && (
            <Badge 
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive/10"
              onClick={() => setFilters(prev => ({ ...prev, hasFreeTier: false }))}
            >
              Free Tier
              <XIcon className="w-3 h-3" />
            </Badge>
          )}
          {filters.hasApiAccess && (
            <Badge 
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive/10"
              onClick={() => setFilters(prev => ({ ...prev, hasApiAccess: false }))}
            >
              API Access
              <XIcon className="w-3 h-3" />
            </Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm"
            className="text-xs h-6 px-2"
            onClick={clearFilters}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Disclaimer */}
      <Card className="mb-6 bg-muted/50 border-border/50">
        <CardContent className="flex items-start gap-3 py-4">
          <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Important information</p>
            <p>
              These results are based on the information you provided and general platform characteristics. 
              Information may change over time. This is not advice — please verify current offerings 
              and suitability for your specific needs before making decisions.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Results grid */}
      {paginatedResults.length > 0 ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            {paginatedResults.map((platform) => (
              <PlatformCard
                key={platform.id}
                platform={platform}
                isSelected={selectedForComparison.includes(platform.id)}
              />
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              <div className="flex items-center gap-1">
                {/* Show page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first, last, current, and neighbors
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, idx, arr) => {
                    // Add ellipsis
                    const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                    return (
                      <div key={page} className="flex items-center">
                        {showEllipsisBefore && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-9 h-9 p-0"
                        >
                          {page}
                        </Button>
                      </div>
                    );
                  })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card className="mb-8 py-12">
          <CardContent className="text-center">
            <SlidersHorizontal className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">No platforms match your filters</p>
            <p className="text-sm text-muted-foreground mb-4">
              Try adjusting your filter criteria to see more results.
            </p>
            <Button variant="outline" onClick={clearFilters}>
              Clear all filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Comparison bar - fixed at bottom when items selected */}
      {selectedForComparison.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 shadow-lg animate-fade-in z-50">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{selectedForComparison.length} selected</Badge>
              <span className="text-sm text-muted-foreground">
                Select up to 3 platforms to compare
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearComparison}
              >
                Clear
              </Button>
              <Button
                size="sm"
                disabled={selectedForComparison.length < 2}
                onClick={navigateToComparison}
                className="gap-1.5"
              >
                <Scale className="w-4 h-4" />
                Compare side-by-side
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className={cn(
        'flex items-center justify-between pt-4 border-t border-border',
        selectedForComparison.length > 0 && 'mb-20'
      )}>
        <Button
          variant="ghost"
          onClick={goToPrevious}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to questions
        </Button>

        <Button
          variant="outline"
          onClick={resetWizard}
          className="gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Start over
        </Button>
      </div>
    </div>
  );
};
